import React from "react";
import {
  Row,
  Col,
  Card,
  Spinner,
  Table,
  Badge,
  ProgressBar,
} from "react-bootstrap";
import { CheckCircle, AlertTriangle, List, Users, Globe } from "react-feather";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useGet } from "../../api";
import { IQaDashboardMetrics } from "../../interfaces";
import { format, formatDistanceToNow } from "date-fns";

export const QaDashboard: React.FC = () => {
  const { data, loading, error } = useGet<IQaDashboardMetrics>(
    "qa-lists/dashboard",
    true
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-5 text-muted">
        <AlertTriangle size={32} className="mb-2" />
        <p>Failed to load QA metrics</p>
      </div>
    );
  }

  const progressPct =
    data.totalItems > 0
      ? Math.round((data.testedItems / data.totalItems) * 100)
      : 0;

  return (
    <>
      {/* Summary cards */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <List size={28} className="text-primary" />
              <div>
                <div className="text-muted small">Total Lists</div>
                <h4 className="mb-0">{data.totalLists}</h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <Globe size={28} className="text-info" />
              <div>
                <div className="text-muted small">Total Items</div>
                <h4 className="mb-0">{data.totalItems}</h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <CheckCircle size={28} className="text-success" />
              <div>
                <div className="text-muted small">Tested</div>
                <h4 className="mb-0">{data.testedItems}</h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <AlertTriangle size={28} className="text-warning" />
              <div>
                <div className="text-muted small">Untested</div>
                <h4 className="mb-0">{data.untestedItems}</h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Progress bar */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between mb-1">
            <span className="fw-bold">Overall Progress</span>
            <span>{progressPct}%</span>
          </div>
          <ProgressBar
            now={progressPct}
            variant={
              progressPct >= 80
                ? "success"
                : progressPct >= 50
                  ? "info"
                  : "warning"
            }
            style={{ height: 12 }}
          />
        </Card.Body>
      </Card>

      <Row className="mb-4 g-3">
        {/* By country */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>
              <Globe size={16} className="me-1" /> By Country
            </Card.Header>
            <Card.Body>
              {data.testedByCountry.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.testedByCountry}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="countryCode" />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="tested"
                      stackId="a"
                      fill="#28a745"
                      name="Tested"
                    />
                    <Bar
                      dataKey="untested"
                      stackId="a"
                      fill="#ffc107"
                      name="Untested"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted text-center py-4">No data</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* By assignee */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>
              <Users size={16} className="me-1" /> By Assignee
            </Card.Header>
            <Card.Body>
              {data.testedByAssignee.length > 0 ? (
                <Table size="sm" hover>
                  <thead>
                    <tr>
                      <th>Assignee</th>
                      <th className="text-end">Total</th>
                      <th className="text-end">Tested</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.testedByAssignee.map((a) => {
                      const pct =
                        a.total > 0
                          ? Math.round((a.tested / a.total) * 100)
                          : 0;
                      return (
                        <tr key={a.assignee}>
                          <td>{a.assigneeName}</td>
                          <td className="text-end">{a.total}</td>
                          <td className="text-end">{a.tested}</td>
                          <td style={{ width: 120 }}>
                            <ProgressBar
                              now={pct}
                              variant={pct >= 80 ? "success" : "warning"}
                              style={{ height: 8 }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <div className="text-muted text-center py-4">No data</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent activity */}
      <Card>
        <Card.Header>Recent Activity</Card.Header>
        <Card.Body>
          {data.recentActivity.length > 0 ? (
            <Table size="sm" hover>
              <thead>
                <tr>
                  <th>List</th>
                  <th>Domain</th>
                  <th>Country</th>
                  <th>Tested By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {data.recentActivity.map((a, i) => (
                  <tr key={i}>
                    <td>{a.listName}</td>
                    <td>
                      <code>{a.domain}</code>
                    </td>
                    <td>
                      <Badge bg="secondary">{a.countryCode}</Badge>
                    </td>
                    <td>{a.testedByName}</td>
                    <td>
                      <small title={format(new Date(a.testedAt), "PPpp")}>
                        {formatDistanceToNow(new Date(a.testedAt), {
                          addSuffix: true,
                        })}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted text-center py-4">
              No recent activity
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
